/**
 * ConfirmDeleteTenantDialog Component
 *
 * Destructive WARNING modal for permanently deleting a customer tenant (org).
 *
 * Deleting a tenant cascades in ZITADEL to every user, metadata entry, app, and
 * grant the org owns — irreversible. Mirroring the CLI's typed confirmation and
 * the member-delete dialog, the admin must type the tenant's name before the red
 * confirm button enables, so it can't be triggered by a reflexive click.
 *
 * @module components/ConfirmDeleteTenantDialog
 */

import { useCallback, useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Button,
  Alert,
  Box,
  TextField,
} from '@mui/material';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import type { TenantDetails } from '../types';
import { formatErrorMessage } from '../utils';

// =============================================================================
// Types
// =============================================================================

export interface ConfirmDeleteTenantDialogProps {
  /**
   * The tenant to delete. `null` keeps the dialog closed; a non-null value opens
   * it — keeping target and open state in lockstep.
   */
  tenant: TenantDetails | null;

  /** Called when the dialog should close (cancel or after a successful delete). */
  onClose: () => void;

  /**
   * Async handler that performs the deletion. Resolves on success; rejects on
   * failure (the dialog shows an inline alert and stays open to retry).
   */
  onConfirm: (orgId: string) => Promise<void>;
}

// =============================================================================
// Component
// =============================================================================

export function ConfirmDeleteTenantDialog({
  tenant,
  onClose,
  onConfirm,
}: ConfirmDeleteTenantDialogProps) {
  const [error, setError] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [confirmText, setConfirmText] = useState('');

  const open = tenant !== null;

  const [prevOpen, setPrevOpen] = useState(open);
  if (open !== prevOpen) {
    setPrevOpen(open);
    if (open) {
      setError(null);
      setIsSubmitting(false);
      setConfirmText('');
    }
  }

  // Type-to-confirm the tenant name (case-insensitive, trimmed).
  const confirmMatches =
    tenant !== null &&
    confirmText.trim().toLowerCase() === tenant.name.trim().toLowerCase();
  const canConfirm = confirmMatches && !isSubmitting;

  const handleCancel = useCallback(() => {
    if (isSubmitting) return;
    onClose();
  }, [isSubmitting, onClose]);

  const handleConfirm = useCallback(async () => {
    if (!tenant || !confirmMatches || isSubmitting) return;
    setIsSubmitting(true);
    setError(null);
    try {
      await onConfirm(tenant.id);
      onClose();
    } catch (err) {
      setError(formatErrorMessage(err));
      setIsSubmitting(false);
    }
  }, [tenant, confirmMatches, isSubmitting, onConfirm, onClose]);

  return (
    <Dialog
      open={open}
      onClose={handleCancel}
      maxWidth="xs"
      fullWidth
      data-testid="delete-tenant-dialog"
      aria-labelledby="delete-tenant-dialog-title"
    >
      <DialogTitle
        id="delete-tenant-dialog-title"
        data-testid="delete-tenant-dialog-title"
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1,
          color: 'error.main',
        }}
      >
        <WarningAmberIcon color="error" />
        Delete tenant permanently
      </DialogTitle>

      <DialogContent>
        {error && (
          <Alert
            severity="error"
            sx={{ mb: 2 }}
            data-testid="delete-tenant-error"
          >
            {error}
          </Alert>
        )}

        {tenant && (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <Alert severity="warning" data-testid="delete-tenant-warning">
              This <strong>permanently deletes</strong> the tenant{' '}
              <strong>{tenant.name}</strong> and{' '}
              <strong>cannot be undone</strong>. All {tenant.userCount} user
              {tenant.userCount === 1 ? '' : 's'}, its metadata, and its
              applications are destroyed.
            </Alert>

            <DialogContentText data-testid="delete-tenant-dialog-text">
              To confirm, type the tenant name <strong>{tenant.name}</strong>{' '}
              below.
            </DialogContentText>

            <TextField
              label="Type the tenant name to confirm"
              value={confirmText}
              onChange={(e) => setConfirmText(e.target.value)}
              disabled={isSubmitting}
              autoFocus
              fullWidth
              autoComplete="off"
              data-testid="delete-tenant-confirm-field"
              slotProps={{
                htmlInput: {
                  'data-testid': 'delete-tenant-confirm-input',
                  'aria-label': 'Type the tenant name to confirm',
                },
              }}
            />
          </Box>
        )}
      </DialogContent>

      <DialogActions>
        <Button
          onClick={handleCancel}
          disabled={isSubmitting}
          data-testid="delete-tenant-cancel-button"
        >
          Cancel
        </Button>
        <Button
          onClick={handleConfirm}
          variant="contained"
          color="error"
          disabled={!canConfirm}
          data-testid="delete-tenant-confirm-button"
        >
          {isSubmitting ? 'Deleting…' : 'Delete tenant'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
